/**
 * ChecklistTemplateEditor (editable-workspace-processes, PR3) — the per-process
 * checklist template editor (reachable from edit mode + the drawer's checklist
 * block). Lists template items grouped by stage mapping; add / re-label /
 * re-tier / re-parent / re-map / reorder / delete.
 *
 * Tier vocabulary (docs/reference/design-language + StageChecklist discipline): the word
 * "gate" is NEVER rendered. The three tiers read:
 *   optional  -> "if applicable"
 *   required  -> "required"
 *   gate      -> "done before moving on"
 *
 * Every consequential edit (add / re-label / re-tier / re-map / delete) routes
 * through askConfirm — the shared dry-run -> real-numbers -> commit path — so the
 * founder sees the impact first. Pure reorder (up/down) commits directly (it
 * never resets a tick or changes a count).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus } from 'lucide-react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import { workspaceProcessesApi, type TemplateItemInput, type WorkProcessWire, type WorkProcessStageWire, type WorkTemplateItemWire } from '@/lib/api';
import { useTemplateItems, useProcessCommand } from '@/lib/workspaceProcesses';
import type { AskConfirm } from './ProcessEditor';
import { SelectField } from '@/components/primitives/SelectField';

type Tier = 'optional' | 'required' | 'gate';
const UNMAPPED = '__unmapped';

function tierOf(item: { is_gate: boolean; is_optional: boolean }): Tier {
  if (item.is_gate) return 'gate';
  if (item.is_optional) return 'optional';
  return 'required';
}
function tierFlags(tier: Tier): { is_gate: boolean; is_optional: boolean } {
  return { is_gate: tier === 'gate', is_optional: tier === 'optional' };
}
const TIER_LABEL: Record<Tier, string> = {
  optional: 'if applicable',
  required: 'required',
  gate: 'done before moving on',
};

export interface ChecklistTemplateEditorProps {
  slug: string;
  process: WorkProcessWire;
  activeStages: WorkProcessStageWire[];
  askConfirm: AskConfirm;
  onClose: () => void;
}

export function ChecklistTemplateEditor({ slug, process, activeStages, askConfirm, onClose }: ChecklistTemplateEditorProps): ReactNode {
  const templateId = process.template_id!;
  const { items, isLoading } = useTemplateItems(slug, templateId);
  const command = useProcessCommand<unknown>(slug);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingStageKey, setAddingStageKey] = useState<string | null>(null);

  const orderedStages = useMemo(() => [...activeStages].sort((a, b) => a.sort_order - b.sort_order), [activeStages]);
  const stageLabel = useMemo(() => new Map(orderedStages.map((s) => [s.key, s.label])), [orderedStages]);

  // Group items by stage key (in board order), plus a trailing Unmapped bucket.
  const byStage = useMemo(() => {
    const m = new Map<string, WorkTemplateItemWire[]>();
    for (const s of orderedStages) m.set(s.key, []);
    m.set(UNMAPPED, []);
    for (const it of [...items].sort((a, b) => a.sort_order - b.sort_order)) {
      const key = it.maps_to_stage_key && m.has(it.maps_to_stage_key) ? it.maps_to_stage_key : UNMAPPED;
      m.get(key)!.push(it);
    }
    return m;
  }, [items, orderedStages]);

  const groupKeys = [...orderedStages.map((s) => s.key), UNMAPPED].filter((k) => (byStage.get(k)?.length ?? 0) > 0 || k !== UNMAPPED);

  const askItem = (opts: {
    title: string; intro?: string; confirmLabel: string; destructive?: boolean;
    dryRun: () => Promise<{ impact: import('@/lib/api').DryRunImpact }>;
    commit: () => Promise<unknown>;
    onDone?: () => void;
  }): void => {
    askConfirm({
      title: opts.title,
      intro: opts.intro,
      confirmLabel: opts.confirmLabel,
      destructive: opts.destructive,
      dryRun: opts.dryRun,
      commit: async () => { const r = await opts.commit(); opts.onDone?.(); return r; },
    });
  };

  const reorder = (item: WorkTemplateItemWire, dir: -1 | 1): void => {
    const group = byStage.get(item.maps_to_stage_key && byStage.has(item.maps_to_stage_key) ? item.maps_to_stage_key : UNMAPPED) ?? [];
    const idx = group.findIndex((g) => g.id === item.id);
    const target = group[idx + dir];
    if (!target) return;
    command.mutate({ run: () => workspaceProcessesApi.updateTemplateItem(slug, item.id, { sort_order: target.sort_order }) });
  };

  return (
    <Dialog title="Checklist steps" size="lg" testId="template-editor" onClose={onClose}>
      <p className="text-xs text-ink-soft mb-4">
        These are the steps every card on “{process.label}” starts with. Editing them updates open cards too; you’ll see how many before it’s applied.
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-soft">Loading…</p>
      ) : (
        <div className="space-y-4" data-testid="template-editor-groups">
          {groupKeys.map((groupKey) => (
            <div key={groupKey} data-testid={`template-group-${groupKey}`}>
              <div className="text-[11px] uppercase tracking-wider text-sage-400 font-bold mb-1.5">
                {groupKey === UNMAPPED ? 'Not on a stage yet' : (stageLabel.get(groupKey) ?? groupKey)}
              </div>
              <div className="space-y-1">
                {(byStage.get(groupKey) ?? []).map((item, i, arr) =>
                  editingId === item.id ? (
                    <ItemForm
                      key={item.id}
                      item={item}
                      stages={orderedStages}
                      siblingItems={items.filter((x) => x.id !== item.id)}
                      onCancel={() => setEditingId(null)}
                      onSave={(input) =>
                        askItem({
                          title: 'Update this step?',
                          confirmLabel: 'Save step',
                          dryRun: () => workspaceProcessesApi.updateTemplateItem(slug, item.id, input, true),
                          commit: () => workspaceProcessesApi.updateTemplateItem(slug, item.id, input),
                          onDone: () => setEditingId(null),
                        })
                      }
                    />
                  ) : (
                    <div key={item.id} className="flex items-center gap-2 py-1" data-testid={`template-item-${item.id}`}>
                      <div className="flex flex-col">
                        <button type="button" aria-label="Move up" data-testid={`template-item-up-${item.id}`} disabled={i === 0 || command.isPending} onClick={() => reorder(item, -1)}
                          className="text-sage-400 hover:text-sage-800 disabled:opacity-30 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"><ChevronUp size={13} aria-hidden="true" /></button>
                        <button type="button" aria-label="Move down" data-testid={`template-item-down-${item.id}`} disabled={i === arr.length - 1 || command.isPending} onClick={() => reorder(item, 1)}
                          className="text-sage-400 hover:text-sage-800 disabled:opacity-30 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"><ChevronDown size={13} aria-hidden="true" /></button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={'text-[13.5px] ' + (item.parent_id ? 'text-ink-soft' : 'text-ink')}>
                          {item.parent_id && <span aria-hidden="true" className="text-sage-300 mr-1">↳</span>}
                          {item.label}
                        </span>
                        <span data-testid={`template-item-tier-${item.id}`} className="ml-2 text-[10.5px] text-sage-400">{TIER_LABEL[tierOf(item)]}</span>
                      </div>
                      <button type="button" data-testid={`template-item-edit-${item.id}`} aria-label={`Edit ${item.label}`} onClick={() => setEditingId(item.id)}
                        className="text-sage-400 hover:text-sage-800 p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"><Pencil size={13} aria-hidden="true" /></button>
                      <button type="button" data-testid={`template-item-delete-${item.id}`} aria-label={`Remove ${item.label}`}
                        onClick={() =>
                          askItem({
                            title: 'Remove this step?',
                            intro: `“${item.label}” will be removed from the checklist.`,
                            confirmLabel: 'Remove step',
                            destructive: true,
                            dryRun: () => workspaceProcessesApi.deleteTemplateItem(slug, item.id, true),
                            commit: () => workspaceProcessesApi.deleteTemplateItem(slug, item.id),
                          })
                        }
                        className="text-sage-400 hover:text-pepper p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"><Trash2 size={13} aria-hidden="true" /></button>
                    </div>
                  ),
                )}

                {addingStageKey === groupKey ? (
                  <ItemForm
                    stages={orderedStages}
                    siblingItems={items}
                    defaultStageKey={groupKey === UNMAPPED ? null : groupKey}
                    onCancel={() => setAddingStageKey(null)}
                    onSave={(input) =>
                      askItem({
                        title: 'Add this step?',
                        confirmLabel: 'Add step',
                        dryRun: () => workspaceProcessesApi.addTemplateItem(slug, templateId, input, true),
                        commit: () => workspaceProcessesApi.addTemplateItem(slug, templateId, input),
                        onDone: () => setAddingStageKey(null),
                      })
                    }
                  />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Plus size={12} aria-hidden="true" />}
                    data-testid={`template-add-${groupKey}`}
                    onClick={() => setAddingStageKey(groupKey)}
                  >
                    Add a step
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-5">
        <Button size="sm" variant="secondary" onClick={onClose} data-testid="template-editor-close">Done</Button>
      </div>
    </Dialog>
  );
}

/** The inline add/edit form (label + tier + input type + stage + optional parent). */
type ItemType = 'checkbox' | 'date' | 'text';
const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  checkbox: 'Checkbox',
  date: 'Date entry',
  text: 'Text entry',
};

function ItemForm({
  item, stages, siblingItems, defaultStageKey, onCancel, onSave,
}: {
  item?: WorkTemplateItemWire;
  stages: WorkProcessStageWire[];
  siblingItems: WorkTemplateItemWire[];
  defaultStageKey?: string | null;
  onCancel: () => void;
  onSave: (input: TemplateItemInput) => void;
}): ReactNode {
  const [label, setLabel] = useState(item?.label ?? '');
  const [tier, setTier] = useState<Tier>(item ? tierOf(item) : 'required');
  // 0159: the step's input shape — a checkbox tick, or a date/text entry that
  // completes when filled ("Date questions sent" style steps). Distinct from
  // the tier "Kind" (a date step can still be required or a stage-blocker).
  const [itemType, setItemType] = useState<ItemType>(
    item?.item_type === 'date' || item?.item_type === 'text' ? item.item_type : 'checkbox',
  );
  const [stageKey, setStageKey] = useState<string>(item?.maps_to_stage_key ?? defaultStageKey ?? (stages[0]?.key ?? ''));
  const [parentId, setParentId] = useState<string>(item?.parent_id ?? '');

  // Nest-under options: other items already on the same stage (server validates
  // the parent belongs to the template).
  const parentOptions = siblingItems.filter((s) => (s.maps_to_stage_key ?? '') === stageKey && !s.parent_id);

  const canSubmit = label.trim().length > 0;

  return (
    <form
      className="rounded-card border border-sage-200 bg-sand/50 p-2.5 space-y-2"
      data-testid="template-item-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSave({
          label: label.trim(),
          ...tierFlags(tier),
          item_type: itemType,
          maps_to_stage_key: stageKey || null,
          parent_id: parentId || null,
        });
      }}
    >
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        data-testid="item-form-label"
        placeholder="Step name"
        className="w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm"
      />
      <div className="flex flex-wrap gap-2">
        <label className="text-[12px] text-ink-soft">
          Kind
          <SelectField size="compact" value={tier} onChange={(e) => setTier(e.target.value as Tier)} data-testid="item-form-tier"
            className="ml-1">
            <option value="optional">{TIER_LABEL.optional}</option>
            <option value="required">{TIER_LABEL.required}</option>
            <option value="gate">{TIER_LABEL.gate}</option>
          </SelectField>
        </label>
        <label className="text-[12px] text-ink-soft">
          Input
          <SelectField size="compact" value={itemType} onChange={(e) => setItemType(e.target.value as ItemType)} data-testid="item-form-input-type"
            className="ml-1">
            {(Object.keys(ITEM_TYPE_LABEL) as ItemType[]).map((t) => (
              <option key={t} value={t}>{ITEM_TYPE_LABEL[t]}</option>
            ))}
          </SelectField>
        </label>
        <label className="text-[12px] text-ink-soft">
          Stage
          <SelectField size="compact" value={stageKey} onChange={(e) => { setStageKey(e.target.value); setParentId(''); }} data-testid="item-form-stage"
            className="ml-1">
            {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </SelectField>
        </label>
        {parentOptions.length > 0 && (
          <label className="text-[12px] text-ink-soft">
            Nest under
            <SelectField size="compact" value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="item-form-parent"
              className="ml-1">
              <option value="">Nothing</option>
              {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </SelectField>
          </label>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" type="submit" data-testid="item-form-save" disabled={!canSubmit}>{item ? 'Save' : 'Add'}</Button>
        <TextAction onClick={onCancel}>Cancel</TextAction>
      </div>
    </form>
  );
}
